import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Center, Grid, GridItem, Spinner, Text, VStack } from '@chakra-ui/react';
import { ArticleCard } from '@src/components/sco/Education/ArticleLanding/ArticleCard';
import { FounderInterviewCard } from '@src/components/sco/Education/ArticleLanding/FounderInterviewCard';
import { useEducationContext } from '@src/components/sco/Education/EducationContext';
import { FadeRevealWithSSR } from '@src/components/sco/ssr-transitions/FadeRevealWithSSR';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { gql, gqlArgs, gqlVars } from '@src/lib/gql';
import React from 'react';
type ArticleListProps = {
  articleType: string;
  keywords?: string;
  showSummaries?: boolean;
  termSlugs?: string[];
  typeSlug?: string;
  typeTitle?: string;
  onResults?: (hasResults: boolean) => void;
};
export const ArticleList = props => {
  const $ = _c(24);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] !== props) {
    t0 = ArticleList.queries.articles(props);
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = props.articleType || "ExpertAdvice";
  let t2;
  if ($[2] !== props.termSlugs) {
    t2 = props.termSlugs || [];
    $[2] = props.termSlugs;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== props.keywords || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      variables: {
        articleType: t1,
        keywords: props.keywords,
        termSlugs: t2
      }
    };
    $[4] = props.keywords;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const query = useQuery(t0, t3);
  useEducationContext();
  if (query.loading || !query.data) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Center><Spinner /></Center>;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (query.error) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <>Error</>;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  if (query.data?.articles.length === 0) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Center padding={8} width="full"><Text color="primary" textStyle="labelMd">No results found for this topic</Text></Center>;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t4;
  if ($[11] !== getBreakpointValue || $[12] !== props.articleType || $[13] !== props.typeSlug || $[14] !== query.data) {
    t4 = () => query.data?.articles && [...query.data.articles].sort(_temp).map((article, index) => {
      let colSpan = 1;
      if (getBreakpointValue([index % 10 === 0 || index % 10 === 5, index % 10 === 0 || index % 10 === 5, index % 10 === 4 || index % 10 === 5, index % 10 === 0 || index % 10 === 3 || index % 10 === 4 || index % 10 === 7 || index % 10 === 8 || index % 10 === 9])) {
        colSpan = 2;
      }
      return <React.Fragment key={article.id}>{props.articleType === "ExpertAdvice" && <GridItem key={article.id} colSpan={getBreakpointValue([1, 1, colSpan])} marginBottom={getBreakpointValue([10])}><FadeRevealWithSSR duration={1500}><ArticleCard authors={true} avatar={article.authors[0]?.avatar?.url} firstName={article.authors[0]?.firstName} image={article.featuredImage?.url} lastName={article.authors[0]?.lastName} subject={article.subjectNames.join(", ")} subjectLogo={article.subjectLogos[0]?.url} summary={article.seoDescription} title={article.title} url={`/${props.typeSlug || "articles"}/${article.slug}`} large={getBreakpointValue([null, null, null, index % 10 === 0 || index % 10 === 3 || index % 10 === 4 || index % 10 === 7 || index % 10 === 8 || index % 10 === 9 ? true : false])} ratio={getBreakpointValue([null, null, null, index % 10 === 1 || index % 10 === 6 ? 1.3333333333333333 : null])} ratioLg={getBreakpointValue([null, null, null, index % 10 === 0 || index % 10 === 7 ? 1.5555555555555556 : null])} /></FadeRevealWithSSR></GridItem>}{props.articleType === "FounderStory" && <GridItem key={article.id} colSpan={getBreakpointValue([1])} marginBottom={getBreakpointValue([10])}><FadeRevealWithSSR duration={1500}><FounderInterviewCard image={article.featuredImage?.url} large={index % 3 === 2} subject={article.subjectNames && article.subjectNames.join(", ")} subjectLogo={article.subjectLogos && article.subjectLogos[0]?.url} summary={article.seoDescription} title={article.title} url={`/${props.typeSlug || "founder-interviews"}/${article.slug}`} /></FadeRevealWithSSR></GridItem>}</React.Fragment>;
    });
    $[11] = getBreakpointValue;
    $[12] = props.articleType;
    $[13] = props.typeSlug;
    $[14] = query.data;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const renderGridItems = t4;
  let t5;
  if ($[16] !== getBreakpointValue || $[17] !== props.articleType) {
    t5 = props.articleType === "FounderStory" ? "repeat(1, 1fr)" : getBreakpointValue(["repeat(1, 1fr)", "repeat(1, 1fr)", "repeat(2, 1fr)", "repeat(4, 1fr)", "repeat(4, 1fr)"]);
    $[16] = getBreakpointValue;
    $[17] = props.articleType;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== renderGridItems) {
    t6 = renderGridItems();
    $[19] = renderGridItems;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5 || $[22] !== t6) {
    t7 = <VStack align="flex-start" width="full"><Grid gap={10} width="full" templateColumns={t5}>{t6}</Grid></VStack>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
};
ArticleList.fragments = {
  Article: () => gql`
    fragment _ on Article {
      id
      title
      synopsis
      teaser
      seoDescription
      slug
      terms {
        id
        name
        slug
        color {
          hex
        }
      }
      subjectNames
      subjectLogos {
        id
        url
      }
      featuredImage {
        id
        url(transformation: { image: { resize: { fit: max, width: 1000 } } })
        altText
        height
        width
      }
      authors {
        id
        firstName
        lastName
        avatar {
          id
          url
        }
      }
    }
  `
};
ArticleList.queries = {
  articles: (props: ArticleListProps) => {
    const vars = gqlVars({
      '$keywords: String': props.keywords,
      '$termSlugs: [String]': props.termSlugs?.length > 0,
      '$articleType: ArticleType': props.articleType
    });
    const where = gqlArgs({
      'where: ': {
        'type: $articleType': props.articleType,
        '_search: $keywords': props.keywords,
        'terms_some: { slug_in: $termSlugs }': props.termSlugs?.length > 0
      }
    });
    return gql`
        query ${vars} {
            articles(
              orderBy: publishedAt_DESC
              ${where}
            ) {
                id
                ${ArticleList.fragments.Article()}
            }
        }
    `;
  }
};
function _temp(a, b) {
  if (b.subjectLogos[0]?.url && !a.subjectLogos[0]?.url) {
    return 1;
  }
  if (a.subjectLogos[0]?.url && !b.subjectLogos[0]?.url) {
    return -1;
  }
  return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
}